ActionProviderService.$inject = [
	'$q',
	'mx.SolutionBuilderAgent.Http',
	'mx.shell.DetailsView',
	'mx.SolutionBuilderAgent.WizardService',
	'mx.shell.Config',
	'mx.shell.UserProfileService',
	'mx.internationalization',
	'mx.SolutionBuilderAgent.ContentWidgetService',
	'mx.SolutionBuilderAgent.RecentService',
	'mx.sb.DialogView.DialogFactory',
	'mx.SolutionBuilderAgent.EntityService',
	'mx.shell.NotificationService',
	'mx.SolutionBuilderAgent.PersonalizationService',
];

export function ActionProviderService(
	$q,
	mxHttp,
	DetailsView,
	wizardService,
	configService,
	userProfileService,
	i18n,
	contentWidgetService,
	recentService,
	dialogFactory,
	entityService,
	notificationService,
	personalizationService
) {
	var getActionsPromise;
	var cache = { data: {} };
	var actionSetDefaultMailingEngineId = '449f4fc5-8b2d-cf45-fdf5-08d594ae65e4';

	return {
		getActions: getActions,
		checkAvailability: checkAvailability,
	};

	async function checkAvailability(action, selectedObjects) {
		if (!action || !action.id || action.isService || !selectedObjects || selectedObjects.length === 0) {
			return { isAvailable: true };
		}

		const objects = selectedObjects.map(function (obj) {
			return {
				ObjectId: obj['Sys-ObjectId2'] || obj._id || obj['Sys-ObjectId'] || obj.ObjectId || obj.objectId || obj.Id || obj.ID || obj.ObjectId,
				TypeName: obj['Sys-EntityType'] || obj.EntityType || obj['Sys-Entity'] || obj.ConfigurationItemTypeName || obj._type || obj.TypeName,
			};
		});

		return mxHttp.post('api/actions/check/' + action.id, objects).then(function (result) {
			return {
				isAvailable: result.IsAvailable,
				cacheId: result.CacheId,
			};
		});
	}

	function getActionsFromService(forceLoad) {
		var doCacheActions = (configService.get('skipCaching') || {}).actions !== true;
		var serverActionsKey = 'mx.SolutionBuilderAgent.ActionsCache';

		if (doCacheActions && !forceLoad) {
			var actions = cache.data[serverActionsKey];

			if (actions) {
				return actions;
			}
		}

		cache.data[serverActionsKey] = mxHttp.post('api/actions/entity', { Types: [] }).then(function (actions) {
			return (actions || []).filter(function (action) {
				return action.Id !== actionSetDefaultMailingEngineId;
			});
		});

		return cache.data[serverActionsKey];
	}

	function getActions(param) {
		var forceLoad = param === true;

		if (getActionsPromise && !forceLoad) {
			return getActionsPromise;
		}

		getActionsPromise = userProfileService.get().then(function () {
			return $q.all([getActionsFromService(forceLoad), contentWidgetService.getAllViews(forceLoad)]).then(function (result) {
				var views = result[1] || [];

				var actions;
				actions = result[0].map(function (item) {
					var action = new mx.shell.Action(item.Name, item.Title, item.Icon);

					action.id = item.Id;

					action.availableTypes = item.Types || [];
					action.states = item.States;
					action.typesFilter = item.TypeFilter || mx.shell.ActionFilterType.Any;
					action.isService = item.IsService || false;
					action.statesFilter = item.StatesFilter;
					action.scriptFilter = item.ScriptFilter;
					action.applications = item.Applications && typeof item.Applications === 'string' ? item.Applications.split(',') : null;

					action.contextType =
						typeof item.ObjectContextType === 'undefined'
							? mx.shell.ActionContextType.None
							: item.ObjectContextType === 1
							? mx.shell.ActionContextType.Single
							: mx.shell.ActionContextType.Multiple;
					action.position = item.Position;
					action.controller = item.Controller;
					action.controllerParams = item.ControllerParams;
					action.uiZones = item.UIZones;
					action.purpose = item.Purpose;

					action.deviceVisibility =
						item.DeviceVisibility === 1
							? mx.shell.ActionDeviceVisibility.DesktopOnly
							: item.DeviceVisibility === 2
							? mx.shell.ActionDeviceVisibility.MobileOnly
							: mx.shell.ActionDeviceVisibility.All;

					action.serviceOperation = item.ServiceOperation;

					action.reloadPageOnSuccess = item.ReloadPageOnSuccess;

					if (item.ImplementationType === 'u') {
						action.controller = function () {
							return {
								execute: function (selectedObjects, action) {
									executeOpenUrlAction(selectedObjects, action, item);
								},
							};
						};
					} else {
						if (item.Widget) {
							var title = item.Title;

							if (item.Widget.Type === mx.SolutionBuilderAgent.ContentWidgetType.Wizard) {
								var wizardOptions = {
									typeName: item.Widget.Entity,
									widgetId: item.Widget.Id,
									actionId: action.id,
									actionName: action.name,
									wizardMode: mx.SolutionBuilderAgent.WizardMode.Default,
									name: title,
									displayName: title,
									finishTitle: title,
								};
								action.view = null;
								action.viewFn = function (selectedObjects, presetFields) {
									wizardOptions.presetFields = presetFields;
									wizardOptions.checkActionAvailability = function (selectedObjects) {
										return checkAvailability(action, selectedObjects);
									};
									var wizardView = wizardService.getWizardView(wizardOptions);
									if (Array.isArray(selectedObjects) && selectedObjects.length <= 1) {
										return userProfileService.isAdminMode().then(function (isAdmin) {
											if (isAdmin) {
												contentWidgetService.setViewUrl(wizardView, undefined, selectedObjects[0]);
											}
											return wizardView;
										});
									} else {
										return $q.when(wizardView);
									}
								};
							} else if (item.Widget.Type === mx.SolutionBuilderAgent.ContentWidgetType.Dialog) {
								action.view = null;
								action.Widget = item.Widget;

								if (item.ImplementationType === 'd') {
									setEditDialog(action, views);
								}
							} else {
								action.view = null;
								action.viewFn = function () {
									return $q.when(
										new DetailsView({
											name: title,
											displayName: title,
											templateUrl: item.Widget.Url,
											controller: item.Widget.Controller,
										})
									);
								};
							}
						} else {
							if (item.ImplementationType === 'd') {
								setEditDialog(action, views);

								action.availableTypes = getSortedEditViews(views)
									.map(function (view) {
										return view.Entity;
									})
									.filter(function (type) {
										switch (action.typesFilter) {
											case mx.shell.ActionFilterType.Specified:
												return action.availableTypes.indexOf(type) >= 0;
											case mx.shell.ActionFilterType.ExceptSpecified:
												return action.availableTypes.indexOf(type) < 0;
											default:
												return true;
										}
									});

								action.typesFilter = mx.shell.ActionFilterType.Specified;
							}
						}
					}

					return action;
				});

				//TODO: use widget-manager
				var groupedViewsByType = _.groupBy(views, 'Entity');
				var extraActions = getExtraActions(groupedViewsByType);
				actions = configureDeleteActions(groupedViewsByType, actions);
				actions = actions.concat(extraActions);

				return actions;
			});
		});

		return getActionsPromise;
	}

	function executeOpenUrlAction(objects, action, item) {
		var url = item.Url.replace(/(?:{([^}]+)})/gi, function (a, g1) {
			return action.contextType === mx.shell.ActionContextType.Single
				? new Function('object, action', 'return ' + g1)(objects[0], action)
				: action.contextType === mx.shell.ActionContextType.None
				? new Function('action', 'return ' + g1)(action)
				: new Function('objects, action', 'return ' + g1)(objects, action);
		});

		window.open(url);
	}

	function setEditDialog(action, views) {
		action.useSameView = true;
		action.openInFullScreen = true;

		action.viewFn = async function (selectedObjects, presetFields) {
			const actionView = await getActionViewForEditDialog(selectedObjects, action, presetFields, views);
			if (!actionView) {
				const errorMessage = i18n.get('solutionBuilderAgent.errors.no_view');
				notificationService.error(errorMessage);
				throw new Error(errorMessage);
			}
			if (actionView.ShowAsWizard && !actionView.alwaysFullScreen) {
				action.openInFullScreen = false;
			}
			return actionView;
		};
	}

	function getExtraActions(groupedViewsByType) {
		var extraActions = [
			createLandingPageAction(
				'pandora.PDRLandingPageWidget.edit-dashboard',
				i18n.get('solutionBuilderAgent.actions.edit-dashboard'),
				'settings',
				'mx.SolutionBuilderAgent.EditDashboardController',
				10
			),
		];

		var type;

		for (type in groupedViewsByType) {
			if (groupedViewsByType.hasOwnProperty(type)) {
				var viewsForType = groupedViewsByType[type];
				var addViews = getSortedCreateViews(viewsForType);
				var isService = type.indexOf('service_') === 0;

				if (addViews.length > 0) {
					var view = addViews[0];
					var actionTitle = i18n.getFormatted('solutionBuilderAgent.common.addPattern', view.EntityDisplayName ? ' ' + view.EntityDisplayName : '');
					const genericAddAction = new mx.shell.Action('generic.new' + type, actionTitle, view.IconAdd || 'add');
					genericAddAction.isService = isService;
					genericAddAction.position = view.AddActionPosition || 10;
					genericAddAction.uiZones = [mx.shell.ActionUiZone.SearchPage];
					genericAddAction.deviceVisibility = 'All';
					genericAddAction.purpose = mx.shell.ActionPurpose.Create;
					genericAddAction.controller = 'mx.SolutionBuilderAgent.NewController';
					genericAddAction.availableTypes = [type];
					genericAddAction.openInFullScreen = true;
					genericAddAction.typesFilter = mx.shell.ActionFilterType.Specified;
					genericAddAction.isPrimary = true;

					extraActions.push(genericAddAction);
				}
			}
		}

		return extraActions;
	}

	async function getActionViewForEditDialog(selectedObjects, action, presetFields, views) {
		const context = {
			_type: selectedObjects && selectedObjects[0] && (selectedObjects[0]._type || selectedObjects[0]['Sys-Entity']),
			_id: selectedObjects && selectedObjects[0] && (selectedObjects[0]._id || selectedObjects[0]['Sys-ObjectId'].toLowerCase()),
			_displayName:
				(selectedObjects && selectedObjects[0] && (selectedObjects[0]._displayName || selectedObjects[0]['Sys-DisplayName'])) ||
				i18n.get('solutionBuilderAgent.common.edit'),
			_name: 'Edit',
		};
		await entityService.processObjectContext(context);

		if (!context._type) {
			return;
		}

		const suitableViews = getSortedEditViews(views, context._type);
		let actionView = null;

		const personalizedViewId = await personalizationService.getDefaultEditDialogId(selectedObjects[0]._type);
		if (personalizedViewId) {
			actionView = suitableViews.find((v) => v.Id === personalizedViewId);
		}

		if (!actionView) {
			if (action?.Widget) {
				actionView = _.find(views, { Id: action.Widget.Id });
			} else {
				actionView = suitableViews && suitableViews[0];
			}
		}

		if (!actionView) {
			return;
		}

		if (actionView.ShowAsWizard === true || !recentService.enabled) {
			// TODO: figure out why this mode accepts preset fields differently
			let presets;
			if (presetFields) {
				presets = {};
				presets[context._type] = presetFields;
			}

			const actionName = action.Title || action.title;
			const itemWizard = {
				typeName: context._type,
				widgetId: actionView.Id,
				actionId: actionView.Id,
				actionName: actionName,
				wizardMode: mx.SolutionBuilderAgent.WizardMode.Edit,
				name: actionName,
				mergeDisabled: actionView.MergeDisabled,
				presetFields: presets,
			};

			const view = wizardService.getWizardView(itemWizard);
			view.alwaysFullScreen = false;
			view.headerIcon = actionView.EntityIcon;
			view.headerColor = actionView.EntityColor;
			view.headerTitle = actionView.EntityDisplayName;
			view.type = context._type;
			view.id = context._id;
			view.ShowAsWizard = true;
			view.dialogType = mx.SolutionBuilderAgent.ContentWidgetDialogAction.Edit;
			contentWidgetService.setViewUrl(view, actionView.Id, context);
			return view;
		}

		return dialogFactory.createEditDialog(context, actionView, {
			presetFields: presetFields,
		});
	}

	function configureDeleteActions(groupedViewsByType, actions) {
		const typesAllowedForDelete = [];
		const servicesForDelete = [];

		for (let type in groupedViewsByType) {
			if (groupedViewsByType.hasOwnProperty(type)) {
				const viewsForType = groupedViewsByType[type];
				const canDelete = viewsForType.length > 0 ? viewsForType[0].CanDelete : false;
				const arrayForAdd = type.indexOf('service_') === 0 ? servicesForDelete : typesAllowedForDelete;
				if (canDelete && arrayForAdd.indexOf(type) === -1) {
					arrayForAdd.push(type);
				}
			}
		}

		actions.forEach(function (value) {
			let actionTypes;
			if (value.name === 'Deleteobjects') {
				actionTypes = value.availableTypes !== null ? value.availableTypes : [];

				value.availableTypes =
					value.typesFilter === mx.shell.ActionFilterType.ExceptSpecified
						? typesAllowedForDelete.filter(function (item) {
								return actionTypes.indexOf(item) === -1;
						  })
						: _.intersection(actionTypes, typesAllowedForDelete);
				value.typesFilter = mx.shell.ActionFilterType.Specified;
			} else if (value.name === 'Deleteforservice') {
				actionTypes = value.availableTypes !== null ? value.availableTypes : [];

				value.availableTypes =
					value.typesFilter === mx.shell.ActionFilterType.ExceptSpecified
						? servicesForDelete.filter(function (item) {
								return actionTypes.indexOf(item) === -1;
						  })
						: _.intersection(actionTypes, servicesForDelete);
				value.typesFilter = mx.shell.ActionFilterType.Specified;
			}
		});
		return actions;
	}

	function createLandingPageAction(name, title, icon, controller, position) {
		const action = new mx.shell.Action(name, title, icon, controller, position);

		action.position = position;
		action.uiZones = [mx.shell.ActionUiZone.LandingPage];
		action.deviceVisibility = 'All';
		action.controller = controller;
		action.availableTypes = ['PDRContentWidgetTypeLandingPage'];
		action.openInFullScreen = true;
		action.typesFilter = mx.shell.ActionFilterType.Specified;

		return action;
	}

	function getSortedCreateViews(viewsForType) {
		//TODO: remove it and use from widgetManager
		return viewsForType
			.filter(function (view) {
				return view.WidgetType === mx.SolutionBuilderAgent.ContentWidgetType.Dialog && view.Purpose !== 2 && !view.NotUseInCreateEditPurposes;
			})
			.sort(function (view1, view2) {
				return view2.Priority - view1.Priority;
			});
	}

	function getSortedEditViews(views, typeName) {
		//TODO: remove it and use from widgetManager
		return views
			.filter(function (view) {
				const isDialog = view.WidgetType === mx.SolutionBuilderAgent.ContentWidgetType.Dialog;
				const isCreateOnlyDialog = view.Purpose === 1;
				const typeMatches = !typeName || view.Entity === typeName;

				return isDialog && !isCreateOnlyDialog && typeMatches && !view.NotUseInCreateEditPurposes;
			})
			.sort(function (view1, view2) {
				return view2.Priority - view1.Priority;
			});
	}
}
